import { computed, inject, Injectable, Signal } from '@angular/core';
import { NGXLogger } from 'ngx-logger';

import { RetailCrmService } from '../../customer/clients/retail-crm/retail-crm.service';
import { DealerConsent, DealerConsentUpdate } from '../../customer/model/customer-information.model';
import { Customer } from '../../customer/model/customer.model';
import { useI18n } from '../../i18n/services/i18n.service';
import { ConsentMapper } from '../mapper/consent.mapper';
import { ConsentSelection } from '../model/consent-checkbox';
import { ConsentLegalText } from '../model/nsc-consent-legal-text';

@Injectable({
  providedIn: 'root',
})
export class DealerConsentService {
  private readonly retailCrmService = inject(RetailCrmService);
  private readonly consentMapper = inject(ConsentMapper);
  private readonly logger = inject(NGXLogger);
  private readonly i18n = useI18n();
  readonly dealerLegalText: Signal<ConsentLegalText> = computed(() => ({
    header: {
      text: this.i18n('consent.dealerLegalHeader'),
      isHtml: false,
    },
    afterUsageDef: {
      text: this.i18n('consent.dealerLegalAfterUsageDef'),
      isHtml: false,
    },
  }));

  applyDealerConsentSelection(
    dealerConsent: DealerConsentUpdate | undefined,
    consentSelection: ConsentSelection | undefined,
  ): DealerConsentUpdate {
    if (!consentSelection) {
      throw new Error('#CDFTF0113 Could not set dealer consent, as consent selection is undefined');
    }

    if (!dealerConsent) {
      this.logger.warn(
        '#CDFTF0107 Initializing dealer consents before setting consent selection as dealer consents were undefined',
      );
      dealerConsent = this.createNewDealerConsent();
    }

    return {
      ...dealerConsent,
      consent: {
        email: consentSelection['email'] ?? false,
        inCar: consentSelection['inCar'] ?? false,
        letter: consentSelection['letter'] ?? false,
        phoneMessenger: consentSelection['phoneMessenger'] ?? false,
        customerProfile: consentSelection['customerProfile'] ?? false,
        dataProcessing: consentSelection['dataProcessing'] ?? false,
      },
    };
  }

  setDealerLegalEntitiesSelection(
    dealerConsent: DealerConsentUpdate | undefined,
    selectedDistributedPartnerNrs: string[] | undefined,
  ): DealerConsentUpdate {
    if (!dealerConsent) {
      this.logger.warn(
        '#CDFTF0167 Initializing dealer consents before setting consent selection as dealer consents were undefined',
      );
      dealerConsent = this.createNewDealerConsent();
    }

    return {
      ...dealerConsent,
      legalEntities: selectedDistributedPartnerNrs,
    };
  }

  async saveDealerConsent(
    customer: Customer | undefined,
    dealerConsentUpdate: DealerConsentUpdate | undefined,
  ): Promise<Customer> {
    if (!customer) {
      throw new Error('#CDFTF0105 Could not save dealer consents as customer is undefined');
    }

    // Update id and date of consent
    if (!dealerConsentUpdate) {
      throw new Error('#CDFTF0110 Dealer consent is not defined while trying to confirm dealer consent');
    }

    // If data processing is not selected, no consents are selected
    if (!dealerConsentUpdate.consent.dataProcessing) {
      dealerConsentUpdate.consent = this.getDefaultDealerConsent();
    }

    const dealerConsent = this.processConsentUpdate(dealerConsentUpdate, customer.accountNumber);
    // Only the consent is sent: for a synced customer the NSC Database owns the master data, so a
    // consent save must not write it back into Retail CRM (CUSTAPP-597 / CUSTAPP-613).
    await this.retailCrmService.updateDealerConsents(customer.accountNumber, dealerConsent);

    // Set new date and id after update was successful
    return { ...customer, dealerConsent };
  }

  createNewDealerConsent(): DealerConsentUpdate {
    return {
      consent: this.consentMapper.mapToDealerConsentOptions(this.getDefaultDealerConsent()),
      legalEntities: undefined,
    };
  }

  generateDateString(): string {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  generateConsentId(accountNumber: string): string {
    const now = new Date();
    const datePart = now.toISOString().split('T')[0].replace(/-/g, '');
    const timePart = now.toTimeString().split(' ')[0].replace(/:/g, '');

    return `${accountNumber}_${datePart}_${timePart}/CDF`;
  }

  processConsentUpdate(dealerConsent: DealerConsentUpdate, accountNumber: string): DealerConsent {
    return {
      ...dealerConsent,
      consentDate: this.generateDateString(),
      consentId: this.generateConsentId(accountNumber),
    };
  }

  getDefaultDealerConsent() {
    return {
      email: false,
      inCar: false,
      letter: false,
      phoneMessenger: false,
      customerProfile: false,
      dataProcessing: false,
    };
  }
}
